import SectionTitle from '../../components/SectionTitle';
import { Fragment } from 'react';
import RowContainer from '../../components/RowContainer';
import RowContent from './components/RowContent';
import { Ti18nId } from 'hooks/useI18n';
import { IFormRegister, IFormValues, IFormWatch } from 'routes/Main/type';
import Form from 'components/Form';
import { Stack, Typography } from '@mui/material';
import styled from 'styled-components';

interface Props extends IFormValues, IFormWatch, IFormRegister {
  disabled?: boolean;
  onRequired: (id: Ti18nId) => void;
  onSuccess: (message: string) => void;
}

const checks = [
  { label: '수술', key: 'select_category.surgery' },
  { label: '검사', key: 'select_category.screening' },
  { label: '마취', key: 'select_category.anesthesia' },
];

// 텍스트 입력 필드
const textFields = [
  { title: '병명', key: 'disease_name', childrenRatio: 10.5 },
  { title: '수술 / 검사명', key: 'surgery_name', childrenRatio: 10.5 },
  {
    title: '주치의 (설명의사) 성명',
    key: 'main_doctor_name',
    childrenRatio: 4.5,
  },
  { title: '서명', key: 'main_doctor_sign', childrenRatio: 4.5 },
];

// 라디오 + 직접입력 필드
const radioFields = [
  {
    title: '기왕력',
    selectKey: 'past_disease.select',
    contentKey: 'past_disease.content',
  },
  {
    title: '알레르기',
    selectKey: 'allergy.select',
    contentKey: 'allergy.content',
  },
  {
    title: '특이체질',
    selectKey: 'special_constitution.select',
    contentKey: 'special_constitution.content',
  },
  {
    title: '당뇨병',
    selectKey: 'diabete.select',
    contentKey: 'diabete.content',
  },
  {
    title: '고혈압, 저혈압',
    selectKey: 'blood_pressure.select',
    contentKey: 'blood_pressure.content',
  },
  {
    title: '출혈소인',
    selectKey: 'blood_cause.select',
    contentKey: 'blood_cause.content',
  },
  {
    title: '심장병',
    selectKey: 'heart_disease.select',
    contentKey: 'heart_disease.content',
  },
  {
    title: '마약사고',
    selectKey: 'drug_accident.select',
    contentKey: 'drug_accident.content',
  },
  {
    title: '약으로 인한 사고',
    selectKey: 'drug_accident.select',
    contentKey: 'drug_accident.content',
  },
];

// 주소/전화/주민번호 입력 필드
const bottomFields = [
  { title: '주소', key: 'address' },
  { title: '전화번호', key: 'phone' },
  { title: '주민등록번호', key: 'personal_number' },
];

const TextBox = styled.div`
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  padding: 8px;
  margin: 30px 0 20px 30px;
`;

export default function Agreement(props: Props) {
  const { disabled, register, getValues, setValue } = props;

  return (
    <Fragment>
      <SectionTitle title="수술동의서" />
      <RowContainer xs={11.5} sx={{ margin: '20px 0px 30px 0px' }}>
        {/* 체크박스 그룹 */}
        <RowContent title="항목 선택" titleRatio={1.5} childrenRatio={10.5}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '30%' }}>
            {checks.map(item => (
              <Form.MuiCheckbox
                key={item.key}
                label={item.label}
                disabled={disabled}
                defaultValue={Boolean(getValues(item.key)) ? [item.label] : []}
                onChange={(_, checked) => setValue(item.key, checked)}
              />
            ))}
          </div>
        </RowContent>

        {/* 수술 시작 날짜 및 On Call */}
        <RowContent
          title="수술 시작 날짜"
          titleRatio={1.5}
          childrenRatio={10.5}
        >
          <Stack direction="row" spacing={2}>
            <Form.MuiTextField
              type="date"
              disabled={disabled}
              {...register('surgery_start_date')}
              sx={{ width: '40rem' }}
            />
            <Form.MuiCheckbox
              label="On Call"
              disabled={disabled}
              defaultValue={getValues('on_call') === 'true' ? ['On Call'] : []}
              onChange={(_, checked) => setValue('on_call', checked)}
            />
          </Stack>
        </RowContent>

        {/* 주요 텍스트 필드 (병명, 수술/검사명, 주치의, 서명) */}
        {textFields.map(field => (
          <RowContent
            key={field.key}
            title={field.title}
            titleRatio={1.5}
            childrenRatio={field.childrenRatio}
          >
            <Form.MuiTextField
              type="text"
              disabled={disabled}
              {...register(field.key)}
            />
          </RowContent>
        ))}

        {/* 본문 설명 텍스트 */}
        <TextBox>
          <Typography sx={{ fontSize: '13px' }}>
            본인은 본인(또는 환자)에 대한 수술(검사·마취)의 필요성, 내용,
            예상되는 합병증, 후유증 등에 대 하여 의사로부터 설명을 들었으며, 본
            수술(검사·마취)로서 불가항력적으로 야기될 수 있는 합병증 또는 환자의
            특이체질로 우발적 사고가 일어날 수도 있다는 것을 사전 설명으로
            충분히 이해하며 수술(검사·마취)에 협력할 것을 서약하고, 다음 사항을
            성실히 알리며 이에 따른 의학적 처리를 주치 의 판단에 위임하여
            수술(검사·마취)을 하는데 동의합니다.
          </Typography>
        </TextBox>

        {/* 라디오 + 직접입력 필드 */}
        {radioFields.map(field => (
          <RowContent
            key={field.selectKey + field.title}
            title={field.title}
            titleRatio={1.5}
            childrenRatio={4.5}
          >
            <Stack direction="row" spacing={2}>
              <Form.MuiRadioGroup
                i18nKey="YESORNOEN"
                values={[1, 2]}
                disabled={disabled}
                defaultValue={getValues(field.selectKey)}
                onChange={v => setValue(field.selectKey, v)}
              />
              <Form.MuiTextField
                required={false}
                disabled={disabled}
                placeholder="직접입력"
                {...register(field.contentKey)}
              />
            </Stack>
          </RowContent>
        ))}

        {/* 추가 안내 */}
        <TextBox>
          <Typography
            sx={{
              fontSize: '13px',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <span>
              ■ 상기 의사의 상세한 설명은 별지를 사용하며 환자가 본 동의서
              사본을 원하면 교부할 수 있다.
            </span>
            <span>
              ■ 본 동의서는 본인의 서명이나 날인으로 유효하나 본인이 서명하기
              어려운 신체적, 정신적 지장이 있거나 또는 미성년자일 경우에는
              보호자 또는 대리인이 이를 대행한다.
            </span>
          </Typography>
        </TextBox>

        {/* 주소/전화/주민등록번호 */}
        {bottomFields.map(field => (
          <RowContent
            key={field.key}
            title={field.title}
            titleRatio={1.5}
            childrenRatio={10.5}
          >
            <Form.MuiTextField
              type="text"
              disabled={disabled}
              {...register(field.key)}
            />
          </RowContent>
        ))}
      </RowContainer>
    </Fragment>
  );
}
